import type { FocusEvent } from 'react'
import type { FieldErrors } from '@tino-frontend/form'
import type { LimitReservation } from './checkout'
import type { InvoiceStatus } from './invoice'

export type PaymentLinkOrigin =
  | 'link-salesperson'
  | 'link-financialperson'
  | 'link-trial'

export type PaymentLinkOrderData = {
  external_id: string
  supplier_company_id: string
  requester_user_id: string
  merchant_document_number: string
  amount_cents: number
  contact: string
  origin: PaymentLinkOrigin
}

export type CreatePaymentLinkPayload = {
  external_id: string
  supplier_company_id: string
  requester_user_id: string
  merchant_document_number: string
  amount_cents: number
  phone_number?: string
  email?: string
  contact?: string
  origin: PaymentLinkOrigin
  confirmed_by_salesperson: boolean
  picadinho_on_salesperson?: boolean
}

export type CreatePaymentLinkFormData = CreatePaymentLinkPayload & {
  is_merchant_setted_by_default: boolean
}

export type CreatePaymentLinkResponse = {
  link: string
  id: string
}

export type ValidatePaymentLinkFormData = {
  supplier_company_id: string
  merchant_document_number: string
  amount_cents: number
  origin: PaymentLinkOrigin
  contact: string
}

export type ValidatePaymentLinkPayload = {
  supplier_company_id: string
  merchant_document_number: string
  amount_cents: number
  origin: PaymentLinkOrigin
}

export type ValidatePaymentLinkResponse = {
  is_valid: boolean
  merchant_document_number: string
  amount_cents: number
}

export type LastOrderResponse = {
  phone_number?: string
  email?: string
  max_due_day: number
  zip_code: string
  street_number: string
  address: string
  state: string
  city: string
  chosen_payment_option: number[]
}

export enum PaymentLinkError {
  BAD_REQUEST = 'payment-link/bad-request',
  INTERNAL = 'payment-link/internal',
  MERCHANT_NOT_FOUND = 'payment-link/merchant-not-found',
  NOT_AUTHORIZED = 'payment-link/not-authorized',
  NOT_VALID_DATA = 'payment-link/not-valid-data',
  NOT_VALID_DOCUMENT_NUMBER = 'payment-link/not-valid-document-number',
  COMPANY_NOT_FOUND = 'payment-link/company-not-found',
}

export type PaymentLink = {
  created_at: string
  external_id: string
  id: string
  merchant_name: string
  status: PaymentLinkStatus
  limit_reservation: LimitReservation | null
  amount_cents: number
}

export type PaymentLinkInvoice = {
  id: string
  nfe_id: string
  created_at: string
  amount_cents: number
  status: InvoiceStatus
}

export type PaymentLinkCashoutStatus = 'pending' | 'settled'

export type PaymentLinkCashout = {
  invoice_id: string
  installment_id: string
  amount_cents: number
  status: PaymentLinkCashoutStatus
  expected_transfer_date: string
}

export type PaymentLinkDetail = {
  id: string
  merchant_name: string
  merchant_document_number: string
  supplier_company_id: string
  external_id: string
  amount_cents: number
  phone_number?: string
  email?: string
  link: string
  link_creation_time: string
  billing_time?: string
  status: PaymentLinkStatus
  limit_reservation_id?: string
  max_due_day: number
  invoices: PaymentLinkInvoice[]
  cashouts?: PaymentLinkCashout[]
  merchant_service_fee_amount_cents?: number
  origin: PaymentLinkOrigin
  street_number?: string
  postal_code?: string
  state?: string
  city?: string
  address?: string
}

export enum PaymentLinkStatus {
  Expired = 'expired',
  LinkSent = 'link_sent',
  PendingNFe = 'pending_nfe',
  Billed = 'billed',
  Canceled = 'canceled',
  PartialBilled = 'partial_billed',
  ReservationExpired = 'reservation_expired',
  WaitingConfirmation = 'waiting_confirmation',
  Rejected = 'rejected',
}

export type ListPaymentLinksFilters = {
  status?: PaymentLinkStatus[]
  ascending_order?: boolean
}

export type ListPaymentLinksPayload = ListPaymentLinksFilters & {
  supplier_company_id: string
  merchant_document_number?: string
  salesperson_user_id?: string
  page_number?: number
  page_size?: number
}

export type ListPaymentLinksResponse = {
  total: number
  payment_links: PaymentLink[]
}

export type ContactPaymentLinkPayload = {
  contact: string
}

export type CreatePaymentLinkSubmitPayload = ContactPaymentLinkPayload &
  CreatePaymentLinkPayload

export type HandleBlur = (
  event: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
  errors: FieldErrors<CreatePaymentLinkSubmitPayload>
) => void

export enum FeedbackStatusPaymentLink {
  CREATE_SUCCESS = 'create_success',
  CREATE_ERROR = 'create_error',
  CREATE_DENIED_BY_FRAUD = 'create_denied_by_fraud',
  VALIDATE_ERROR = 'validate_error',
}
